/**
 * How the student panel writes a date.
 *
 * The long form lives in `features/teacher/utils` and is imported where it is
 * needed rather than copied — one spelling of "07 Aug 2026" across the product
 * (CLAUDE.md §3). Only the short weekday is here, because no other screen
 * prints one.
 */

/** "Tue" — the weekday a `YYYY-MM-DD` falls on. */
export function shortWeekday(day: string): string {
  // Read in UTC, matching how these dates are stored and sent. Parsing one as a
  // local instant is how "2026-08-07" becomes the 6th west of Greenwich.
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    timeZone: 'UTC',
    weekday: 'short',
  });
}

/** Today at the institute, as `YYYY-MM-DD`. */
export function todayString(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

/** The day before a `YYYY-MM-DD`. */
export function previousDay(day: string): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - 1);

  return date.toISOString().slice(0, 10);
}

/**
 * The Monday-to-Sunday week a `YYYY-MM-DD` falls in, both ends inclusive.
 *
 * Indian schools run Monday to Saturday, so the week opens on Monday rather
 * than the Sunday `getDay()` counts from.
 */
export function weekOf(day: string): { from: string; to: string } {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
  const from = date.toISOString().slice(0, 10);
  date.setUTCDate(date.getUTCDate() + 6);

  return { from, to: date.toISOString().slice(0, 10) };
}
